// frontend/src/components/EmpresaPortal/UsuariosEmpresa.js
//
// Portal de empresa > Usuarios: personas con acceso al portal de esta
// empresa. Cualquier usuario puede crear otros (por ahora todos tienen el
// mismo rol: administrador de empresa), hasta el límite que fija el admin de
// PsyTest. Backend: GET/POST /api/empresas/me/usuarios.

import React, { useEffect, useState } from 'react';
import { UserPlus, Users, Mail } from 'lucide-react';

const FORM_VACIO = { nombre: '', email: '', password: '', confirmar: '' };

const fecha = (valor) => (valor ? new Date(valor).toLocaleDateString('es-MX') : null);

const UsuariosEmpresa = () => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState(FORM_VACIO);
  const [feedback, setFeedback] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    try {
      const response = await fetch('/api/empresas/me/usuarios', { credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudieron cargar los usuarios');
      setDatos(data);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => { cargar(); }, []);

  const cambiar = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const crear = async (e) => {
    e.preventDefault();
    setFeedback(null);
    if (form.password.length < 8) {
      setFeedback({ type: 'error', message: 'La contraseña debe tener al menos 8 caracteres.' });
      return;
    }
    if (form.password !== form.confirmar) {
      setFeedback({ type: 'error', message: 'Las contraseñas no coinciden.' });
      return;
    }
    setGuardando(true);
    try {
      const response = await fetch('/api/empresas/me/usuarios', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre: form.nombre, email: form.email, password: form.password })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'No se pudo crear el usuario');
      setFeedback({
        type: 'success',
        message: `Se creó el usuario de ${data.nombre}. Comparte con esa persona su correo (${data.email}) y la contraseña que definiste para que inicie sesión.`
      });
      setForm(FORM_VACIO);
      await cargar();
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setGuardando(false);
    }
  };

  if (error) return <div className="ep-card"><p className="ep-feedback ep-feedback-error">{error}</p></div>;
  if (!datos) return <div className="ep-card"><p className="ep-empty">Cargando usuarios...</p></div>;

  const lleno = datos.total >= datos.limite;

  return (
    <>
      <div className="ep-card">
        <div className="ep-card-header">
          <h2><Users size={18} /> Usuarios de la empresa</h2>
          <span className={`ep-usuarios-conteo ${lleno ? 'lleno' : ''}`}>{datos.total} de {datos.limite} usuarios</span>
        </div>
        <p className="ep-hint">
          Personas que pueden entrar al portal de tu empresa. Todas tienen los mismos permisos: invitar candidatos,
          ver resultados y administrar pruebas y stacks.
        </p>

        <div className="ep-table-wrap">
          <table className="ep-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Creado</th>
                <th>Último acceso</th>
              </tr>
            </thead>
            <tbody>
              {datos.usuarios.map(u => (
                <tr key={u.id}>
                  <td>
                    {u.nombre}
                    {u.es_yo && <span className="ep-usuario-yo">Tú</span>}
                  </td>
                  <td><span className="ep-usuario-email"><Mail size={13} /> {u.email}</span></td>
                  <td>{fecha(u.created_at)}</td>
                  <td>{fecha(u.last_login) || <span className="ep-usuario-nunca">Nunca ha entrado</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ep-card">
        <div className="ep-card-header">
          <h2><UserPlus size={18} /> Agregar usuario</h2>
        </div>
        {lleno ? (
          <p className="ep-feedback ep-feedback-error">
            Tu empresa ya tiene {datos.total} de {datos.limite} usuarios permitidos. Si necesitas agregar más personas,
            contacta al administrador de PsyTest.
          </p>
        ) : (
          <form onSubmit={crear} className="ep-form">
            <div className="ep-field-row">
              <div className="ep-field">
                <label htmlFor="usr-nombre">Nombre completo</label>
                <input id="usr-nombre" name="nombre" type="text" value={form.nombre} onChange={cambiar} required maxLength={150} placeholder="Ej: Ana López" disabled={guardando} />
              </div>
              <div className="ep-field">
                <label htmlFor="usr-email">Correo (para iniciar sesión)</label>
                <input id="usr-email" name="email" type="email" value={form.email} onChange={cambiar} required autoComplete="off" placeholder="ana@empresa.com" disabled={guardando} />
              </div>
            </div>
            <div className="ep-field-row">
              <div className="ep-field">
                <label htmlFor="usr-password">Contraseña</label>
                <input id="usr-password" name="password" type="password" value={form.password} onChange={cambiar} required minLength={8} autoComplete="new-password" placeholder="Mínimo 8 caracteres" disabled={guardando} />
              </div>
              <div className="ep-field">
                <label htmlFor="usr-confirmar">Confirmar contraseña</label>
                <input id="usr-confirmar" name="confirmar" type="password" value={form.confirmar} onChange={cambiar} required minLength={8} autoComplete="new-password" disabled={guardando} />
              </div>
            </div>
            <button type="submit" className="ep-btn-primary" disabled={guardando}>
              <UserPlus size={15} /> {guardando ? 'Creando...' : 'Crear usuario'}
            </button>
          </form>
        )}
        {feedback && <div className={`ep-feedback ep-feedback-${feedback.type}`}>{feedback.message}</div>}
      </div>
    </>
  );
};

export default UsuariosEmpresa;
